import React, { useState, useEffect } from 'react';
import { userAPI } from '../../services/api';

const UserModal = ({ isOpen, onClose, user, onSave, defaultRole = 'client' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: defaultRole,
    city: '',
    specialization: '',
    isWaitlisted: false,
    status: 'active'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isEdit = !!user;

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '', // Don't show password for existing user
        role: user.role || defaultRole,
        city: user.city || '',
        specialization: user.specialization || '',
        isWaitlisted: user.isWaitlisted || false,
        status: user.status || 'active'
      });
    } else {
      setFormData({
        name: '',
        email: '',
        password: '',
        role: defaultRole,
        city: '',
        specialization: '',
        isWaitlisted: false,
        status: 'active'
      });
    }
    setError(null);
  }, [user, defaultRole, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEdit) {
        // Prepare update data (exclude password if empty)
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        
        await userAPI.update(user.id, updateData);
      } else {
        // Create new user
        if (!formData.password) {
          setError('Password is required for new users');
          setLoading(false);
          return;
        }
        await userAPI.create(formData);
      }
      
      onSave();
      onClose();
    } catch (err) {
      console.error('Error saving user:', err);
      setError(err.response?.data?.message || 'Failed to save user details');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-[var(--surface-white)] rounded-[2.5rem] shadow-2xl w-full max-w-lg overflow-hidden border border-[var(--border-light)] flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="p-8 border-b border-[var(--border-light)] flex justify-between items-center bg-gradient-to-r from-[var(--off-white)]/30 to-[var(--surface-white)]">
          <div>
            <h2 className="text-2xl font-black text-[var(--text-primary)] tracking-tight">
              {isEdit ? 'Edit Profile' : `Add New ${defaultRole.charAt(0).toUpperCase() + defaultRole.slice(1)}`}
            </h2>
            <p className="text-xs font-bold text-[var(--text-secondary)]/50 uppercase tracking-widest mt-1">
              {isEdit ? `User ID: ${user.id.substring(0, 8)}...` : 'System Management'}
            </p>
          </div>
          <button 
            onClick={onClose}
            className="w-10 h-10 rounded-full hover:bg-[var(--off-white)] flex items-center justify-center text-[var(--text-secondary)] transition-colors"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="p-8 overflow-y-auto">
          <form id="user-form" onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 p-4 rounded-2xl text-sm font-bold flex items-center gap-2">
                <span className="material-symbols-outlined text-lg">error</span>
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 gap-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-[var(--text-secondary)]/50 uppercase tracking-widest mb-2 ml-1">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-5 py-3.5 bg-[var(--off-white)] border border-[var(--border-light)] text-[var(--text-primary)] rounded-2xl focus:ring-2 focus:ring-tangerine/20 focus:border-tangerine outline-none transition-all font-medium placeholder-[var(--text-secondary)]/30"
                    placeholder="Enter full name"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[var(--text-secondary)]/50 uppercase tracking-widest mb-2 ml-1">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-5 py-3.5 bg-[var(--off-white)] border border-[var(--border-light)] text-[var(--text-primary)] rounded-2xl focus:ring-2 focus:ring-tangerine/20 focus:border-tangerine outline-none transition-all font-medium placeholder-[var(--text-secondary)]/30"
                    placeholder="email@example.com"
                  />
                </div>

                {!isEdit && (
                  <div>
                    <label className="block text-xs font-bold text-[var(--text-secondary)]/50 uppercase tracking-widest mb-2 ml-1">Password</label>
                    <input
                      type="password"
                      name="password"
                      required={!isEdit}
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full px-5 py-3.5 bg-[var(--off-white)] border border-[var(--border-light)] text-[var(--text-primary)] rounded-2xl focus:ring-2 focus:ring-tangerine/20 focus:border-tangerine outline-none transition-all font-medium placeholder-[var(--text-secondary)]/30"
                      placeholder="Enter strong password"
                    />
                  </div>
                )}
              </div>

              {/* Role Specific Info */}
              <div className="space-y-4 pt-4 border-t border-[var(--border-light)]">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-xs font-bold text-[var(--text-secondary)]/50 uppercase tracking-widest mb-2 ml-1">Role</label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      className="w-full px-5 py-3.5 bg-[var(--off-white)] border border-[var(--border-light)] rounded-2xl focus:ring-2 focus:ring-tangerine/20 focus:border-tangerine outline-none transition-all font-bold text-[var(--text-primary)] capitalize"
                    >
                      <option value="client">Client</option>
                      <option value="coach">Coach</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>

                  <div className="flex-1">
                    <label className="block text-xs font-bold text-[var(--text-secondary)]/50 uppercase tracking-widest mb-2 ml-1">Location</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="w-full px-5 py-3.5 bg-[var(--off-white)] border border-[var(--border-light)] text-[var(--text-primary)] rounded-2xl focus:ring-2 focus:ring-tangerine/20 focus:border-tangerine outline-none transition-all font-medium placeholder-[var(--text-secondary)]/30"
                      placeholder="City/Region"
                    />
                  </div>
                </div>

                 {formData.role === 'coach' && (
                  <div className="animate-in slide-in-from-top-2 duration-300">
                    <label className="block text-xs font-bold text-[var(--text-secondary)]/50 uppercase tracking-widest mb-2 ml-1">Specialization</label>
                    <input
                      type="text"
                      name="specialization"
                      value={formData.specialization}
                      onChange={handleChange}
                      className="w-full px-5 py-3.5 bg-[var(--off-white)] border border-[var(--border-light)] text-[var(--text-primary)] rounded-2xl focus:ring-2 focus:ring-tangerine/20 focus:border-tangerine outline-none transition-all font-medium placeholder-[var(--text-secondary)]/30"
                      placeholder="Life Coach, Health, Business..."
                    />
                  </div>
                )}

                <div className="flex flex-col gap-4 animate-in slide-in-from-top-2 duration-300 pt-2">
                  <label className="block text-xs font-bold text-[var(--text-secondary)]/50 uppercase tracking-widest ml-1">User Status</label>
                  <div className="flex gap-4">
                    <label className="flex-1 cursor-pointer">
                      <input
                        type="radio"
                        name="status"
                        value="active"
                        checked={formData.status === 'active'}
                        onChange={handleChange}
                        className="peer hidden"
                      />
                      <div className="flex items-center justify-center gap-2 px-4 py-3 bg-[var(--off-white)] border border-[var(--border-light)] rounded-2xl peer-checked:bg-green-500/10 peer-checked:border-green-500/20 peer-checked:text-green-600 font-bold transition-all text-[var(--text-secondary)]/40">
                        <span className="material-symbols-outlined text-lg">check_circle</span>
                        Active
                      </div>
                    </label>
                    <label className="flex-1 cursor-pointer">
                      <input
                        type="radio"
                        name="status"
                        value="blocked"
                        checked={formData.status === 'blocked'}
                        onChange={handleChange}
                        className="peer hidden"
                      />
                      <div className="flex items-center justify-center gap-2 px-4 py-3 bg-[var(--off-white)] border border-[var(--border-light)] rounded-2xl peer-checked:bg-red-500/10 peer-checked:border-red-500/20 peer-checked:text-red-600 font-bold transition-all text-[var(--text-secondary)]/40">
                        <span className="material-symbols-outlined text-lg">block</span>
                        Blocked
                      </div>
                    </label>
                  </div>
                </div>

                {formData.role === 'client' && (
                  <div className="flex items-center gap-3 px-1">
                    <input
                      type="checkbox"
                      id="isWaitlisted"
                      name="isWaitlisted"
                      checked={formData.isWaitlisted}
                      onChange={handleChange}
                      className="w-5 h-5 rounded-lg border-[var(--border-light)] bg-[var(--off-white)] text-tangerine focus:ring-tangerine outline-none transition-all cursor-pointer"
                    />
                    <label htmlFor="isWaitlisted" className="text-sm font-bold text-[var(--text-secondary)] cursor-pointer">
                      Add to Waitlist
                    </label>
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="p-8 border-t border-[var(--border-light)] bg-[var(--off-white)]/30 flex gap-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-6 py-4 rounded-2xl font-bold text-[var(--text-secondary)] hover:bg-[var(--off-white)] transition-all border border-transparent hover:border-[var(--border-light)]"
          >
            Cancel
          </button>
          <button
            form="user-form"
            type="submit"
            disabled={loading}
            className="flex-3 px-10 py-4 bg-tangerine hover:bg-[#d97700] text-white rounded-2xl font-extrabold shadow-lg shadow-orange-500/30 transition-all hover:-translate-y-1 disabled:opacity-50 disabled:translate-y-0 flex items-center justify-center gap-2"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
              <span className="material-symbols-outlined text-xl">save</span>
            )}
            {isEdit ? 'Save Changes' : 'Create Account'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserModal;
